import React from 'react';

/**
 * ConfirmModal: A custom confirmation dialog that matches the app's aesthetic.
 */
export function ConfirmModal({ isOpen, title, message, onConfirm, onCancel, onClose, confirmText = 'Confirm', cancelText = 'Cancel', variant = 'danger' }) {
    if (!isOpen) return null;
    const close = onCancel || onClose || (() => {});

    return (
        <div className="fixed inset-0 z-[600] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in duration-500">
            <div className="bg-white border border-espresso/10 w-full max-w-xl shadow-[0_3rem_6rem_rgba(0,0,0,0.15)] overflow-hidden animate-in zoom-in-95 duration-500 rounded-[3rem] relative">
                <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-transparent via-(--accent)/20 to-transparent"></div>
                <div className="p-10 space-y-8">
                    <div className="flex items-center gap-6">
                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-2xl border ${variant === 'danger' ? 'border-(--error)/20 text-(--error) bg-(--error)/5' : 'border-(--accent)/20 text-(--accent) bg-(--accent)/5'}`}>
                            {variant === 'danger' ? '⚠️' : '⚡'}
                        </div>
                        <div>
                            <h3 className="text-xl font-black text-(--text) uppercase tracking-tight">{title}</h3>
                            <p className="text-[0.5rem] text-(--text-muted) font-black uppercase tracking-widest mt-1 opacity-40">Verification Protocol Required</p>
                        </div>
                    </div>

                    <p className="text-(--text) text-[0.75rem] font-bold leading-relaxed opacity-70 border-l-2 border-(--border) pl-6 py-1">
                        {message}
                    </p>

                    <div className="flex gap-4 pt-4">
                        <button
                            type="button"
                            onClick={close}
                            className="flex-1 bg-(--surface-stone) text-(--text-muted) font-black text-[0.625rem] uppercase tracking-widest py-4 rounded-2xl hover:bg-(--border) hover:text-(--text) transition-all cursor-pointer transform active:scale-95"
                        >
                            {cancelText}
                        </button>
                        <button
                            type="button"
                            onClick={onConfirm}
                            className={`flex-1 font-black text-[0.625rem] uppercase tracking-widest py-4 rounded-2xl transition-all cursor-pointer transform active:scale-95 shadow-lg ${variant === 'danger' ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-teal-600 text-white hover:bg-teal-700'}`}
                        >
                            {confirmText}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

/**
 * Toast: A simple floating notification.
 */
export function Toast({ message, type = 'success', onClose }) {
    React.useEffect(() => {
        const timer = setTimeout(onClose, 3000);
        return () => clearTimeout(timer);
    }, [onClose]);

    const typeStyles = {
        success: 'border-pass/20 text-pass bg-white/95 backdrop-blur-2xl shadow-[0_2rem_4rem_rgba(0,0,0,0.08)]',
        error: 'border-fail/20 text-fail bg-white/95 backdrop-blur-2xl shadow-[0_2rem_4rem_rgba(0,0,0,0.08)]',
        info: 'border-teal/20 text-teal bg-white/95 backdrop-blur-2xl shadow-[0_2rem_4rem_rgba(0,0,0,0.08)]',
    };

    return (
        <div className={`fixed bottom-12 right-12 z-[500] px-10 py-6 border rounded-[2.5rem] animate-in slide-in-from-right-16 duration-700 flex items-center gap-6 group ${typeStyles[type]}`}>
            <div className={`w-14 h-14 rounded-3xl flex items-center justify-center text-2xl ${type === 'success' ? 'bg-pass/10' : type === 'error' ? 'bg-fail/10' : 'bg-teal/10'}`}>
                {type === 'success' ? '⚡' : type === 'error' ? '🚫' : '📡'}
            </div>
            <div className="flex-1 min-w-[15rem]">
                <p className="text-[0.75rem] font-black uppercase tracking-[0.2em] leading-tight pr-8">{message}</p>
                <div className="mt-3 h-1.5 bg-current opacity-10 rounded-full w-full overflow-hidden">
                    <div className="h-full bg-current animate-progress-shrink rounded-full opacity-60"></div>
                </div>
            </div>
            <button 
                onClick={onClose}
                className="opacity-0 group-hover:opacity-40 hover:!opacity-100 transition-opacity p-2 -mr-2"
            >
                ✕
            </button>
        </div>
    );
}
